import { motion } from 'framer-motion'
import { Kicker } from './Kicker'
import { nextDevWindows } from '@/lib/devMilestones'
import { ageMonths as computeAgeMonths } from '@/lib/types'
import type { Person } from '@/lib/types'

/**
 * DevMilestonesSection — appears on person profile when there's a baby/child
 * under 30 months. Shows the "what to watch for" milestones for the current
 * age window + red flags. Sourced from CDC "Learn the Signs. Act Early."
 */

export function DevMilestonesSection({ person }: { person: Person }) {
  const ageMonths = computeAgeMonths(person)
  if (ageMonths == null || ageMonths < 0 || ageMonths > 30) return null
  const windows = nextDevWindows(ageMonths, 2)
  if (windows.length === 0) return null

  return (
    <section>
      <Kicker>What to watch for (CDC)</Kicker>
      <div className="mt-2 space-y-3">
        {windows.map((w, i) => (
          <motion.div
            key={w.ageMonths}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, delay: i * 0.04 }}
            className="glass-tile p-4"
          >
            <div className="flex items-baseline justify-between gap-3">
              <div className="text-body font-medium text-text-primary">Around {w.headline}</div>
              {i === 0 && (
                <div className="text-tag text-accent-mint">current</div>
              )}
            </div>
            <ul className="mt-3 space-y-1.5 text-caption text-text-secondary">
              {w.milestones.map((m, j) => (
                <li key={j} className="flex gap-2">
                  <span className="text-tag uppercase tracking-wider shrink-0" style={{ color: colorForCategory(m.category), minWidth: 68 }}>
                    {m.category}
                  </span>
                  <span>{m.text}</span>
                </li>
              ))}
            </ul>
            {w.redFlags && w.redFlags.length > 0 && (
              <div className="mt-3 pt-3 border-t border-divider-subtle/40">
                <div className="text-tag font-medium uppercase tracking-wider" style={{ color: '#D14758' }}>
                  Raise with pediatrician if:
                </div>
                <ul className="mt-2 space-y-1 text-caption text-text-secondary">
                  {w.redFlags.map((f, k) => (
                    <li key={k}>· {f}</li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>
        ))}
      </div>
      <div className="mt-2 text-tag text-text-quiet">
        Every baby develops on their own timeline. Source: CDC "Learn the Signs. Act Early." 2022.
      </div>
    </section>
  )
}

function colorForCategory(cat: string): string {
  switch (cat) {
    case 'social':    return '#FF7A2D'
    case 'language':  return '#2ED2A3'
    case 'cognitive': return '#9B7BB8'
    case 'motor':     return '#5BA3E0'
    default:          return '#8B968F'
  }
}
